/** Select do design system — mesma anatomia do `Campo`.
 *
 * Existe porque não existia: os quatro `<select>` da aplicação tinham a classe
 * copiada à mão e nenhum deles igual ao outro — um com `bg-canvas` e outro com
 * `bg-superficie`, `px-2` contra `px-3`, e o rótulo ora em `caption`, ora em
 * micro maiúsculo. No formulário de nova oportunidade os três controles ficavam
 * lado a lado, e a divergência aparecia toda de uma vez.
 *
 * Rótulo, altura, raio, fundo e anel de foco vêm daqui. Componente que precisa
 * de um select usa este; classe solta em tela é o começo da próxima divergência.
 */

import { useId, type SelectHTMLAttributes } from "react";

type Tamanho = "sm" | "md";

const ALTURAS: Record<Tamanho, string> = {
  sm: "h-8 text-caption",
  md: "h-10 text-body",
};

interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "className" | "id"> {
  rotulo: string;
  /** Esconde o rótulo visualmente, mantendo-o para leitor de tela. Para quando
   * o contexto já diz o que o campo é — um seletor dentro do menu do card. */
  rotuloOculto?: boolean;
  tamanho?: Tamanho;
  erro?: string;
}

export function Selecao({
  rotulo,
  rotuloOculto = false,
  tamanho = "md",
  erro,
  children,
  ...resto
}: Props) {
  const id = useId();
  const idErro = `${id}-erro`;
  const temErro = Boolean(erro);

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className={rotuloOculto ? "sr-only" : "font-texto text-caption text-texto-suave"}
      >
        {rotulo}
      </label>

      <select
        {...resto}
        id={id}
        aria-invalid={temErro}
        aria-describedby={temErro ? idErro : undefined}
        className={[
          // `bg-superficie`, e não `bg-canvas`: dentro de um cartão branco o
          // cinza fazia o select parecer desabilitado ao lado dos inputs.
          "rounded-md border bg-superficie px-3 font-texto text-texto",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1",
          "focus-visible:outline-acao-viva",
          "disabled:cursor-not-allowed disabled:opacity-55",
          temErro ? "border-carmim" : "border-borda",
          ALTURAS[tamanho],
        ].join(" ")}
      >
        {children}
      </select>

      {temErro && (
        <p id={idErro} role="alert" className="font-texto text-caption text-carmim">
          {erro}
        </p>
      )}
    </div>
  );
}
